/**
 * D-124 « Demander la revue d'un formateur »: under the results of a finished sitting (TCF, TEF,
 * IELTS, German), each written or spoken work marked by the AI can be sent to a trainer, with an
 * optional reason; the requests included in the period, the promised deadline and the trainer's
 * answer (mark confirmed or changed, comment) are shown. Also the oral credits notice (1 credit per
 * examiner session) for the speaking screens.
 */

import {
  ApiError,
  academyAttemptsTrainerReviewCreate,
  getAcademyAttemptsTrainerReviewRetrieveQueryKey,
  useAcademyAttemptsTrainerReviewRetrieve,
  useAcademyMeOralCreditsRetrieve,
  type ReviewableProduction,
} from "@agencyos/api-client";
import { ORAL_CREDITS_EVENT, formatReviewMark } from "@agencyos/core";
import { Badge, Button, GlassCard, TextAreaField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { Mic, UserCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import "./promises.css";
import { CardTitle } from "./ui";

function useDate(): (value: string | null | undefined, withTime?: boolean) => string {
  const { i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-GB" : "fr-FR";
  return (value, withTime = false) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(locale, {
      day: "numeric",
      month: "long",
      ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
      timeZone: "Africa/Douala",
    });
  };
}

function ProductionRow({ attemptId, production, left }: { attemptId: string; production: ReviewableProduction; left: number }) {
  const { t } = useTranslation();
  const date = useDate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const request = production.request;
  const skill = t(`promises.review.skill.${production.skill === "ee" || production.skill === "eo" ? production.skill : "other"}`);

  async function send() {
    setBusy(true);
    setMessage("");
    try {
      await academyAttemptsTrainerReviewCreate(attemptId, { production: production.id, reason });
      setOpen(false);
      setMessage(t("promises.review.sent"));
      await queryClient.invalidateQueries({ queryKey: getAcademyAttemptsTrainerReviewRetrieveQueryKey(attemptId) });
    } catch (error) {
      setMessage(error instanceof ApiError && error.detail ? error.detail : t("promises.review.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="ac-review-request">
      <div className="ac-review-request__head">
        <strong>{t("promises.review.task", { skill, number: production.task })}</strong>
        <span className="ac-secondary">{t("promises.review.mark", { mark: formatReviewMark(production) })}</span>
      </div>
      {request?.status === "pending" && (
        <p className="ac-status" role="status">
          <Badge tone="info" dot>
            {t("promises.review.pending", { date: date(request.due_at, true) })}
          </Badge>
        </p>
      )}
      {request?.status === "done" && (
        <div className="ac-review-request__answer" role="status">
          <Badge tone="success" dot>
            {t("promises.review.done", { date: date(request.done_at) })}
          </Badge>
          <p>
            {formatReviewMark(request.new_score) === formatReviewMark(request.previous_score)
              ? t("promises.review.confirmed", { mark: formatReviewMark(request.new_score) })
              : t("promises.review.changed", {
                  mark: formatReviewMark(request.new_score),
                  previous: formatReviewMark(request.previous_score),
                })}
          </p>
          {request.trainer_comment && <p>{t("promises.review.comment", { comment: request.trainer_comment })}</p>}
        </div>
      )}
      {!request && !production.eligible && <p className="ac-secondary">{t("promises.review.notEligible")}</p>}
      {!request && production.eligible && !open && (
        <Button variant="secondary" size="sm" disabled={left <= 0} onClick={() => setOpen(true)}>
          {t("promises.review.ask")}
        </Button>
      )}
      {open && (
        <div className="ac-review-request__form">
          <TextAreaField
            label={t("promises.review.reason")}
            hint={t("promises.review.reasonHint")}
            value={reason}
            maxLength={1000}
            rows={3}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="ac-review-request__actions">
            <Button size="sm" loading={busy} onClick={() => void send()}>
              {t("promises.review.send")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              {t("promises.review.cancel")}
            </Button>
          </div>
        </div>
      )}
      {message && (
        <p className="ac-status" role="status">
          {message}
        </p>
      )}
    </li>
  );
}

/** Under the results of a finished sitting: nothing when it has no written or spoken work. */
export function TrainerReviewPanel({ attemptId }: { attemptId: string }) {
  const { t } = useTranslation();
  const date = useDate();
  const query = useAcademyAttemptsTrainerReviewRetrieve(attemptId, { query: { retry: false } });
  const data = query.data?.status === 200 ? query.data.data : undefined;
  const productions = (data?.productions ?? []).filter((p) => p.status === "graded" || p.request);
  if (!data || productions.length === 0) return null;
  const { quota } = data;
  return (
    <section className="ac-page ac-section" aria-labelledby="academy-trainer-review">
      <GlassCard className="ac-card">
        <CardTitle id="academy-trainer-review" icon={<UserCheck size={16} />}>
          {t("promises.review.title")}
        </CardTitle>
        <p>{t("promises.review.intro", { hours: quota.deadline_hours })}</p>
        <p className="ac-secondary">
          {t("promises.review.quota", { count: quota.remaining, total: quota.included })}
          {quota.resets_on ? ` ${t("promises.review.resets", { date: date(quota.resets_on) })}` : ""}
        </p>
        {quota.remaining <= 0 && <p className="ac-secondary">{t("promises.review.noneLeft")}</p>}
        <ul className="ac-review-requests">
          {productions.map((p) => (
            <ProductionRow key={p.id} attemptId={attemptId} production={p} left={quota.remaining} />
          ))}
        </ul>
        <p className="ac-note">{t("promises.review.estimate")}</p>
      </GlassCard>
    </section>
  );
}

/** Speaking screens: credits left and their reset date; a polite message when none is left (the
 * examiner then goes on with its recorded follow-ups, every other activity stays open). */
export function OralCreditsNotice() {
  const { t } = useTranslation();
  const date = useDate();
  const query = useAcademyMeOralCreditsRetrieve({ query: { retry: false, staleTime: 30_000 } });
  const { refetch } = query;
  useEffect(() => {
    const onEmpty = () => void refetch();
    window.addEventListener(ORAL_CREDITS_EVENT, onEmpty);
    return () => window.removeEventListener(ORAL_CREDITS_EVENT, onEmpty);
  }, [refetch]);
  const data = query.data?.status === 200 ? query.data.data : undefined;
  if (!data?.counted) return null;
  const empty = data.remaining <= 0;
  return (
    <p className={`ac-note${empty ? " ac-note--warning" : ""}`} role={empty ? "alert" : "note"}>
      <Mic aria-hidden size={14} />{" "}
      {empty
        ? data.resets_on
          ? t("promises.credits.empty", { date: date(data.resets_on) })
          : t("promises.credits.emptyNoDate")
        : `${t("promises.credits.left", { count: data.remaining, total: data.per_period })}. ${
            data.resets_on ? t("promises.credits.resets", { date: date(data.resets_on) }) : ""
          } ${t("promises.credits.rule")}`}
    </p>
  );
}

/** Called by the speaking screens when the API answers 402 (no credit left). */
export function reportNoOralCredits(): void {
  window.dispatchEvent(new Event(ORAL_CREDITS_EVENT));
}
